import { lazy, Suspense, useEffect } from 'react'
import { Routes, Route, useLocation } from 'react-router-dom'
import LandingPage from './pages/LandingPage'
import LoginPage from './pages/LoginPage'
import RegisterPage from './pages/RegisterPage'
import ProtectedRoute from './components/common/ProtectedRoute'
import Layout from './components/common/Layout'
import PwaUpdateBanner from './components/common/PwaUpdateBanner'
import I18nDocumentLang from './components/common/I18nDocumentLang'

const DashboardPage = lazy(() => import('./pages/DashboardPage'))
const ChildrenManagementPage = lazy(() => import('./pages/ChildrenManagementPage'))
const SchoolStudentsPage = lazy(() => import('./pages/SchoolStudentsPage'))
const ProgressPage = lazy(() => import('./pages/ProgressPage'))
const DrawingPage = lazy(() => import('./pages/DrawingPage'))
const AlbumPage = lazy(() => import('./pages/AlbumPage'))
const StoryPage = lazy(() => import('./pages/StoryPage'))
const GamePage = lazy(() => import('./pages/GamePage'))
const PlaygroundGamePage = lazy(() => import('./pages/PlaygroundGamePage'))
const ActivityPage = lazy(() => import('./pages/ActivityPage'))
const DrawingDetailPage = lazy(() => import('./pages/DrawingDetailPage'))

const FAVICON_PATH = '/favicon.png'

/** Reset window scroll when route changes (pages outside Layout use document scroll). */
function ScrollToTop() {
  const { pathname } = useLocation()
  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'auto' })
    document.documentElement.scrollTop = 0
    document.body.scrollTop = 0
  }, [pathname])
  return null
}

function GlobalHeadAssets() {
  const location = useLocation()

  useEffect(() => {
    const ensureIcon = (rel, href) => {
      let link = document.head.querySelector(`link[rel="${rel}"]`)
      if (!link) {
        link = document.createElement('link')
        link.setAttribute('rel', rel)
        document.head.appendChild(link)
      }
      link.setAttribute('type', 'image/png')
      link.setAttribute('href', href)
    }

    // Re-apply on each route so favicon stays consistent everywhere.
    ensureIcon('icon', FAVICON_PATH)
    ensureIcon('shortcut icon', FAVICON_PATH)
  }, [location.pathname])

  return null
}

function PageFallback() {
  return (
    <div className="min-h-[60vh] flex items-center justify-center">
      <img
        src="/assets/images/ui_elements/icon_loading.png"
        alt=""
        className="w-16 h-16 md:w-24 md:h-24 object-contain animate-spin"
      />
    </div>
  )
}

function App() {
  return (
    <Suspense fallback={<PageFallback />}>
      <I18nDocumentLang />
      <ScrollToTop />
      <GlobalHeadAssets />
      <PwaUpdateBanner />
      <Routes>
        <Route path="/" element={<LandingPage />} />
        <Route path="/login" element={<LoginPage />} />
        <Route path="/register" element={<RegisterPage />} />

        {/* Public story share (UUID) — no login */}
        <Route path="/s/:publicId" element={<StoryPage />} />

        <Route element={<ProtectedRoute><Layout /></ProtectedRoute>}>
          <Route path="/dashboard" element={<DashboardPage />} />
          <Route path="/dashboard/progress" element={<ProgressPage />} />
          <Route path="/children" element={<ChildrenManagementPage />} />
          <Route path="/school/students" element={<SchoolStudentsPage />} />
          <Route path="/draw" element={<DrawingPage />} />
          <Route path="/drawing/:id/play" element={<PlaygroundGamePage />} />
          <Route path="/drawing/:id" element={<DrawingDetailPage />} />
          <Route path="/album" element={<AlbumPage />} />
          <Route path="/story/:id" element={<StoryPage />} />
          <Route path="/game/:id" element={<GamePage />} />
          <Route path="/activity/:id" element={<ActivityPage />} />
        </Route>
      </Routes>
    </Suspense>
  )
}

export default App
